<template>
  <div class="rigid-equilibrium-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ΣF = 0 且 ΣM = 0$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真演示区 -->
      <div class="simulation-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>左侧力矩 (M₁)</label>
            <span class="val">{{ m1.toFixed(1) }} <small>N·m</small></span>
          </div>
          <div class="hud-item pink">
            <label>右侧力矩 (M₂)</label>
            <span class="val">{{ m2.toFixed(1) }} <small>N·m</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240" class="equilibrium-canvas">
          <!-- 地面 -->
          <line x1="40" y1="180" x2="360" y2="180" stroke="#f1f5f9" stroke-width="2" />
          
          <!-- 支点 -->
          <path d="M 200,180 L 185,158 L 215,158 Z" fill="#1e293b" stroke="#334155" stroke-width="2" />

          <!-- 杠杆系统 (绕中心转动) -->
          <g :transform="`rotate(${tiltAngle}, 200, 158)`">
            <!-- 杠杆本体 -->
            <rect x="50" y="152" width="300" height="8" fill="#475569" rx="4" />
            
            <!-- 左侧重物 -->
            <g :transform="`translate(${200 - pos1 * 1.4}, 152)`">
               <rect x="-10" :y="-mass1 * 2.5" width="20" :height="mass1 * 2.5" fill="#3b82f6" rx="2" />
               <text :y="-mass1 * 2.5 - 5" text-anchor="middle" fill="#3b82f6" font-size="10" font-weight="700">{{ mass1 }}kg</text>
               <!-- 重力矢量 -->
               <line x1="0" y1="0" x2="0" y2="30" stroke="#3b82f6" stroke-width="2" marker-end="url(#arrow-blue)" />
            </g>

            <!-- 右侧重物 -->
            <g :transform="`translate(${200 + pos2 * 1.4}, 152)`">
               <rect x="-10" :y="-mass2 * 2.5" width="20" :height="mass2 * 2.5" fill="#f43f5e" rx="2" />
               <text :y="-mass2 * 2.5 - 5" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="700">{{ mass2 }}kg</text>
               <!-- 重力矢量 -->
               <line x1="0" y1="0" x2="0" y2="30" stroke="#f43f5e" stroke-width="2" marker-end="url(#arrow-red)" />
            </g>

            <!-- 连线标注 L1, L2 -->
            <line x1="200" y1="145" :x2="200 - pos1 * 1.4" y2="145" stroke="#94a3b8" stroke-dasharray="4,2" />
            <line x1="200" y1="145" :x2="200 + pos2 * 1.4" y2="145" stroke="#94a3b8" stroke-dasharray="4,2" />
          </g>

          <!-- 平衡状态标识 -->
          <g transform="translate(200, 40)">
             <rect x="-50" y="-12" width="100" height="24" rx="12" :fill="isBalanced ? '#dcfce7' : '#fee2e2'" />
             <text text-anchor="middle" :fill="isBalanced ? '#16a34a' : '#ef4444'" font-size="11" font-weight="900">
               {{ isBalanced ? 'BALANCE / 平衡' : 'UNBALANCED / 倾斜' }}
             </text>
          </g>

          <defs>
            <marker id="arrow-blue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#3b82f6" />
            </marker>
            <marker id="arrow-red" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L6,3 L0,6 Z" fill="#f43f5e" />
            </marker>
          </defs>
        </svg>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card-grid">
          <div class="control-group">
            <div class="control-item">
              <div class="label-row"><span class="label">左侧质量 m₁</span><span class="val">{{ mass1 }} kg</span></div>
              <el-slider v-model="mass1" :min="1" :max="10" size="small"></el-slider>
            </div>
            <div class="control-item">
              <div class="label-row"><span class="label">左侧位置 L₁</span><span class="val">{{ pos1 }} cm</span></div>
              <el-slider v-model="pos1" :min="10" :max="100" size="small"></el-slider>
            </div>
          </div>
          <div class="control-group">
            <div class="control-item">
              <div class="label-row"><span class="label">右侧质量 m₂</span><span class="val">{{ mass2 }} kg</span></div>
              <el-slider v-model="mass2" :min="1" :max="10" size="small"></el-slider>
            </div>
            <div class="control-item">
              <div class="label-row"><span class="label">右侧位置 L₂</span><span class="val">{{ pos2 }} cm</span></div>
              <el-slider v-model="pos2" :min="10" :max="100" size="small"></el-slider>
            </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
          <i class="el-icon-info"></i>
          <p><strong>物理规律：</strong>刚体的平衡需要满足合外力为零（不发生平移）且<strong>绕任意轴的合力矩为零</strong>（不发生转动）。在这个模型中，力矩 M = m · g · L。只有当 M₁ = M₂ 时，杠杆才能维持水平状态。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RigidEquilibriumViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mass1: 5,
      pos1: 60,
      mass2: 3,
      pos2: 100,
      tiltAngle: 0
    }
  },
  computed: {
    m1() { return (this.mass1 * 9.8 * this.pos1) / 100; },
    m2() { return (this.mass2 * 9.8 * this.pos2) / 100; },
    isBalanced() { return Math.abs(this.m1 - this.m2) < 0.5; }
  },
  watch: {
    m1: 'updateTilt',
    m2: 'updateTilt'
  },
  methods: {
    updateTilt() {
      const diff = this.m2 - this.m1;
      // Linear mapping of torque difference to tilt angle (-15 to 15 degrees)
      const target = Math.max(-15, Math.min(15, diff * 2));
      this.animateTo(target);
    },
    animateTo(target) {
      if (this.timer) cancelAnimationFrame(this.timer);
      const step = () => {
        const delta = target - this.tiltAngle;
        if (Math.abs(delta) < 0.1) {
          this.tiltAngle = target;
          return;
        }
        this.tiltAngle += delta * 0.1;
        this.timer = requestAnimationFrame(step);
      };
      step();
    }
  },
  mounted() { this.updateTilt(); },
  beforeDestroy() { if (this.timer) cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.rigid-equilibrium-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(255, 255, 255, 0.9); border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #64748b; font-weight: 700; }
      .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; } }
      &.cyan { border-left: 3px solid #3b82f6; }
      &.pink { border-left: 3px solid #f43f5e; }
    }
  }
}

.equilibrium-canvas { display: block; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.02)); }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0;
  .control-group { display: flex; flex-direction: column; gap: 8px; }
  .control-item {
    .label-row { display: flex; justify-content: space-between; .label { font-size: 12px; color: #64748b; } .val { font-size: 12px; color: #2563eb; font-weight: 700; } }
  }
}

.discovery-card-compact {
  background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px;
  i { color: #16a34a; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } }
}
</style>
